import { useCallback, useEffect, useRef, useState } from 'react';

// Hook that wires up the open/close + multi-pick behaviour of the
// hero search typeahead.
//
// Returns:
//   inputRef        — attach to the search <Input ref={inputRef} ... />
//   isOpen          — pass to <HeroSearchTypeahead open={isOpen} ... />
//   inputProps      — spread onto the <Input> for focus/blur/keydown
//   onTypeaheadPick — wrap the parent's chip-toggle so the input stays
//                     focused (typeahead stays open) for multi-pick
//   closeNow        — call on form submit to close immediately
//
// Why a hook: LandingPage and SearchResultsPage both render the same
// hero bar and want identical open/close behaviour. Inlining the
// state + timers + handlers twice would drift.
export function useHeroTypeahead({ onPick } = {}) {
  const inputRef = useRef(null);
  const blurTimerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  // Tidy up the deferred blur if the component unmounts mid-delay.
  useEffect(
    () => () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    },
    [],
  );

  const handleFocus = useCallback(() => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setIsOpen(true);
  }, []);

  // Defer the close so an in-typeahead click can refocus the input
  // (via onTypeaheadPick below) before we hide the dropdown.
  const handleBlur = useCallback(() => {
    blurTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      blurTimerRef.current = null;
    }, 150);
  }, []);

  // Escape dismisses without unmounting — user can re-focus to reopen.
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setIsOpen(false);
  }, []);

  // Self-heal backstop: typing always reopens. `handleFocus` is the
  // primary opener, but `isOpen` can be stranded `false` while the
  // input still holds focus — e.g. the X-clear path (handleClearQuery)
  // refocuses imperatively while the 150ms deferred-blur timer is still
  // pending, and that timer then fires setIsOpen(false). Keying off
  // input means we can't depend on whether the focus event was caught.
  // Harmless: it only ever opens, and the dropdown's own render guard
  // (trimmed.length >= 2 && matches.length) suppresses an empty list.
  const handleInput = useCallback(() => setIsOpen(true), []);

  const closeNow = useCallback(() => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setIsOpen(false);
  }, []);

  // Wrap the parent's chip-toggle so we cancel the pending blur and
  // refocus the input — typeahead stays open for stacking more chips.
  const onTypeaheadPick = useCallback(
    (key, tagId) => {
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
      onPick?.(key, tagId);
      // Refocus may be a no-op if the parent navigated away — that's
      // fine, the new page mounts a fresh input.
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [onPick],
  );

  return {
    inputRef,
    isOpen,
    inputProps: {
      onFocus: handleFocus,
      onBlur: handleBlur,
      onInput: handleInput,
      onKeyDown: handleKeyDown,
    },
    onTypeaheadPick,
    closeNow,
  };
}
